import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { api } from '../../lib/api.js';

interface Validator {
  id: string;
  name: string;
  deviceId: string;
  lastSyncAt: string | null;
  validatedCount: number;
  revokedAt: string | null;
  createdAt: string;
}

interface ValidatorListResponse {
  validators: Validator[];
}

interface ValidatorCreatedResponse {
  id: string;
  name: string;
  deviceId: string;
  syncToken: string;
}

interface Props {
  eventId: string;
  lang: string;
}

/**
 * Builds the scan setup URL. The syncToken lives in the URL fragment
 * (`#token=…`) so it never hits server access logs as a query string.
 * The page at `/scan/:eventId` reads it from `window.location.hash` on load
 * and stores it in localStorage scoped to the eventId.
 */
function buildScanUrl(eventId: string, syncToken: string, lang: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://boletra.com';
  return `${origin}/${lang}/scan/${eventId}#token=${syncToken}`;
}

export function ValidatorsPanel({ eventId, lang }: Props) {
  const { t, i18n } = useTranslation('dashboard');
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [setupFor, setSetupFor] = useState<ValidatorCreatedResponse | null>(null);
  const [rotateTarget, setRotateTarget] = useState<Validator | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Validator | null>(null);

  const list = useQuery<ValidatorListResponse>({
    queryKey: ['validators', eventId],
    queryFn: () => api<ValidatorListResponse>(`/scanner/devices?eventId=${eventId}`),
    refetchOnWindowFocus: false,
    refetchInterval: 60_000,
  });

  // On-demand fetch of an existing pairing link. Same shape as create/rotate
  // so we can pipe the result straight into <SetupModal>. This is the only
  // path that exposes a *non-rotated* syncToken after creation; we route
  // through it explicitly so the access stays owner-gated by the API.
  const showLinkMutation = useMutation({
    mutationFn: (id: string) => api<ValidatorCreatedResponse>(`/scanner/devices/${id}/setup`),
    onSuccess: (data) => setSetupFor(data),
  });

  const createMutation = useMutation({
    mutationFn: (input: { name: string }) =>
      api<ValidatorCreatedResponse>('/scanner/devices', {
        method: 'POST',
        body: { eventId, name: input.name },
      }),
    onSuccess: (created) => {
      setSetupFor(created);
      setCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['validators', eventId] });
    },
  });

  const rotateMutation = useMutation({
    mutationFn: (id: string) =>
      api<{ id: string; syncToken: string }>(`/scanner/devices/${id}/rotate`, {
        method: 'POST',
      }),
    onSuccess: (res) => {
      const target = rotateTarget;
      if (target) {
        setSetupFor({
          id: res.id,
          name: target.name,
          deviceId: target.deviceId,
          syncToken: res.syncToken,
        });
      }
      setRotateTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['validators', eventId] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api<{ ok: true }>(`/scanner/devices/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setRevokeTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['validators', eventId] });
    },
  });

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  return (
    <section className="card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold">{t('validators.page_title')}</h2>
          <p className="text-sm text-neutral-400 mt-1 max-w-2xl">{t('validators.page_subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="btn-primary text-sm whitespace-nowrap"
        >
          + {t('validators.create_cta')}
        </button>
      </div>

      {list.isLoading && <p className="text-sm text-neutral-500">{t('validators.loading')}</p>}
      {list.isError && <p className="text-sm text-red-400">{t('validators.errors.load')}</p>}

      {list.data && list.data.validators.length === 0 && (
        <div className="rounded-2xl border border-dashed border-neutral-800 p-6 sm:p-10 text-center">
          <h3 className="text-lg font-semibold mb-2">{t('validators.empty_title')}</h3>
          <p className="text-sm text-neutral-400 max-w-md mx-auto mb-5">
            {t('validators.empty_body')}
          </p>
          <button type="button" onClick={() => setCreateOpen(true)} className="btn-primary text-sm">
            {t('validators.create_first_cta')}
          </button>
        </div>
      )}

      {list.data && list.data.validators.length > 0 && (
        <ul className="divide-y divide-neutral-900 -mx-2">
          {list.data.validators.map((v) => (
            <ValidatorRow
              key={v.id}
              validator={v}
              dateFmt={dateFmt}
              onShowLink={() => showLinkMutation.mutate(v.id)}
              showLinkLoadingId={showLinkMutation.isPending ? showLinkMutation.variables : null}
              onRotate={() => setRotateTarget(v)}
              onRevoke={() => setRevokeTarget(v)}
            />
          ))}
        </ul>
      )}
      {showLinkMutation.isError && (
        <p className="text-xs text-red-400 mt-2">{t('validators.setup_modal.load_error')}</p>
      )}

      {createOpen && (
        <CreateModal
          onClose={() => setCreateOpen(false)}
          onSubmit={(name) => createMutation.mutate({ name })}
          submitting={createMutation.isPending}
          error={createMutation.isError ? t('validators.errors.create') : null}
        />
      )}

      {setupFor && (
        <SetupModal
          validator={setupFor}
          lang={lang}
          eventId={eventId}
          onClose={() => setSetupFor(null)}
        />
      )}

      {rotateTarget && (
        <ConfirmModal
          title={t('validators.rotate_modal.title', { name: rotateTarget.name })}
          body={t('validators.rotate_modal.body')}
          submitLabel={t('validators.rotate_modal.submit')}
          submittingLabel={t('validators.rotate_modal.submitting')}
          cancelLabel={t('validators.rotate_modal.cancel')}
          submitting={rotateMutation.isPending}
          error={rotateMutation.isError ? t('validators.errors.rotate') : null}
          variant="warning"
          onCancel={() => setRotateTarget(null)}
          onSubmit={() => rotateMutation.mutate(rotateTarget.id)}
        />
      )}

      {revokeTarget && (
        <ConfirmModal
          title={t('validators.revoke_modal.title', { name: revokeTarget.name })}
          body={t('validators.revoke_modal.body')}
          submitLabel={t('validators.revoke_modal.submit')}
          submittingLabel={t('validators.revoke_modal.submitting')}
          cancelLabel={t('validators.revoke_modal.cancel')}
          submitting={revokeMutation.isPending}
          error={revokeMutation.isError ? t('validators.errors.revoke') : null}
          variant="danger"
          onCancel={() => setRevokeTarget(null)}
          onSubmit={() => revokeMutation.mutate(revokeTarget.id)}
        />
      )}
    </section>
  );
}

function ValidatorRow({
  validator,
  dateFmt,
  onShowLink,
  showLinkLoadingId,
  onRotate,
  onRevoke,
}: {
  validator: Validator;
  dateFmt: Intl.DateTimeFormat;
  onShowLink: () => void;
  showLinkLoadingId: string | null | undefined;
  onRotate: () => void;
  onRevoke: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const revoked = Boolean(validator.revokedAt);
  const showLinkLoading = showLinkLoadingId === validator.id;
  return (
    <li className="px-2 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className={[
              'w-2 h-2 rounded-full',
              revoked
                ? 'bg-red-500/70'
                : validator.lastSyncAt
                  ? 'bg-emerald-500'
                  : 'bg-neutral-600',
            ].join(' ')}
            aria-hidden
          />
          <p className="font-semibold truncate">{validator.name}</p>
          <span
            className={[
              'text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full',
              revoked
                ? 'bg-red-500/10 text-red-300 border border-red-500/30'
                : 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30',
            ].join(' ')}
          >
            {revoked ? t('validators.list_status_revoked') : t('validators.list_status_active')}
          </span>
        </div>
        <p className="text-xs text-neutral-500">
          {t('validators.list_validated', { count: validator.validatedCount })}
        </p>
        <p className="text-xs text-neutral-500 mt-0.5">
          {validator.lastSyncAt
            ? t('validators.list_last_sync', {
                time: dateFmt.format(new Date(validator.lastSyncAt)),
              })
            : t('validators.list_last_sync_never')}
        </p>
        {revoked && validator.revokedAt && (
          <p className="text-xs text-red-400 mt-0.5">
            {t('validators.list_revoked_at', {
              time: dateFmt.format(new Date(validator.revokedAt)),
            })}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2 sm:flex-nowrap">
        {!revoked && (
          <>
            <button
              type="button"
              onClick={onShowLink}
              disabled={showLinkLoading}
              className="btn-secondary text-xs whitespace-nowrap"
            >
              {showLinkLoading
                ? t('validators.actions.loading_link')
                : t('validators.actions.show_link')}
            </button>
            <button
              type="button"
              onClick={onRotate}
              className="btn-secondary text-xs whitespace-nowrap"
            >
              {t('validators.actions.rotate_token')}
            </button>
            <button
              type="button"
              onClick={onRevoke}
              className="text-xs text-red-400 hover:text-red-300 px-3"
            >
              {t('validators.actions.revoke')}
            </button>
          </>
        )}
      </div>
    </li>
  );
}

function CreateModal({
  onClose,
  onSubmit,
  submitting,
  error,
}: {
  onClose: () => void;
  onSubmit: (name: string) => void;
  submitting: boolean;
  error: string | null;
}) {
  const { t } = useTranslation('dashboard');
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const trimmed = name.trim();
  const invalid = touched && trimmed.length === 0;
  return (
    <ModalShell onClose={onClose}>
      <h3 className="text-xl font-semibold mb-1">{t('validators.create_modal.title')}</h3>
      <p className="text-sm text-neutral-400 mb-4">{t('validators.create_modal.subtitle')}</p>
      <label className="block text-sm text-neutral-300 mb-1">
        {t('validators.create_modal.name_label')}
      </label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder={t('validators.create_modal.name_placeholder')}
        autoFocus
        className="input w-full"
      />
      {invalid && (
        <p className="text-xs text-red-400 mt-2">{t('validators.create_modal.name_required')}</p>
      )}
      {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
      <div className="flex justify-end gap-2 mt-5">
        <button
          type="button"
          onClick={onClose}
          className="btn-secondary text-sm"
          disabled={submitting}
        >
          {t('validators.revoke_modal.cancel')}
        </button>
        <button
          type="button"
          onClick={() => {
            if (!trimmed) {
              setTouched(true);
              return;
            }
            onSubmit(trimmed);
          }}
          className="btn-primary text-sm"
          disabled={submitting}
        >
          {submitting
            ? t('validators.create_modal.submitting')
            : t('validators.create_modal.submit')}
        </button>
      </div>
    </ModalShell>
  );
}

function SetupModal({
  validator,
  lang,
  eventId,
  onClose,
}: {
  validator: ValidatorCreatedResponse;
  lang: string;
  eventId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('dashboard');
  const url = buildScanUrl(eventId, validator.syncToken, lang);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    QRCode.toString(url, { type: 'svg', margin: 1, width: 220 })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return (
    <ModalShell onClose={onClose}>
      <h3 className="text-xl font-semibold mb-1">{t('validators.setup_modal.title')}</h3>
      <p className="text-sm text-neutral-400 mb-4">{t('validators.setup_modal.subtitle')}</p>

      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 mb-4">
        <p className="text-sm font-semibold text-amber-300">
          {t('validators.setup_modal.warning_title')}
        </p>
        <p className="text-xs text-amber-200/90 mt-1">{t('validators.setup_modal.warning_body')}</p>
      </div>

      <p className="text-xs text-neutral-500 uppercase tracking-widest font-semibold mb-2">
        {t('validators.setup_modal.name_label')}
      </p>
      <p className="text-sm font-medium mb-4">{validator.name}</p>

      <div className="flex justify-center mb-4">
        {qrSvg ? (
          <div
            className="bg-white p-3 rounded-xl"
            dangerouslySetInnerHTML={{ __html: qrSvg }}
            aria-label="QR code"
          />
        ) : (
          <div className="w-[220px] h-[220px] bg-neutral-900 rounded-xl" />
        )}
      </div>

      <p className="text-xs text-neutral-500 uppercase tracking-widest font-semibold mb-2">
        {t('validators.setup_modal.link_label')}
      </p>
      <div className="flex gap-2 mb-5">
        <input
          type="text"
          value={url}
          readOnly
          className="input flex-1 text-xs font-mono"
          onFocus={(e) => e.currentTarget.select()}
        />
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(url).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            });
          }}
          className="btn-secondary text-xs whitespace-nowrap"
        >
          {copied ? '✓' : t('validators.actions.copy_link')}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row justify-end gap-2">
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary text-sm text-center"
        >
          {t('validators.setup_modal.open_now')}
        </a>
        <button type="button" onClick={onClose} className="btn-primary text-sm">
          {t('validators.setup_modal.done')}
        </button>
      </div>
    </ModalShell>
  );
}

function ConfirmModal({
  title,
  body,
  submitLabel,
  submittingLabel,
  cancelLabel,
  submitting,
  error,
  variant,
  onSubmit,
  onCancel,
}: {
  title: string;
  body: string;
  submitLabel: string;
  submittingLabel: string;
  cancelLabel: string;
  submitting: boolean;
  error: string | null;
  variant: 'warning' | 'danger';
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const submitClass =
    variant === 'danger'
      ? 'bg-red-600 hover:bg-red-500 text-white text-sm px-4 py-2 rounded-lg font-semibold'
      : 'btn-primary text-sm';
  return (
    <ModalShell onClose={onCancel}>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-sm text-neutral-300 mb-4">{body}</p>
      {error && <p className="text-xs text-red-400 mb-3">{error}</p>}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary text-sm"
          disabled={submitting}
        >
          {cancelLabel}
        </button>
        <button type="button" onClick={onSubmit} className={submitClass} disabled={submitting}>
          {submitting ? submittingLabel : submitLabel}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-neutral-950 border border-neutral-800 rounded-t-2xl sm:rounded-2xl shadow-xl p-5 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {children}
      </div>
    </div>
  );
}
